import { useTranslation } from "react-i18next";
import { chooseLanguage, SUPPORTED_LANGUAGES } from "@/shared/language";

/**
 * The EN/DE switch, in the top bar beside the notification bell (Jira NAV-28).
 *
 * In the bar rather than a menu, so somebody looking for German finds it
 * without opening anything. Both shells render it, the member navbar and the
 * admin header, so it sits in the same place wherever the reader is.
 *
 * A group of two toggle buttons rather than a select: there are two languages,
 * and both choices visible at once is the whole point of moving it out of a
 * menu. `aria-pressed` says which one is on, and the group's name says what
 * the two buttons choose between.
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language?.startsWith("de") ? "de" : "en";

  return (
    <div
      role="group"
      aria-label={t("language")}
      className="flex items-center gap-1 rounded-lg bg-muted p-0.5"
      data-testid="language-switcher"
    >
      {SUPPORTED_LANGUAGES.map((lang) => (
        <button
          key={lang}
          type="button"
          // `chooseLanguage`, not `changeLanguage`: this is the one place a
          // person states a preference, and it has to be recorded as one.
          // Without the record their business unit's default (PRD H7) would be
          // re-applied over the top on the next load, and the switch would
          // appear not to stick for exactly the readers whose business unit
          // disagrees with them.
          onClick={() => void chooseLanguage(i18n, lang)}
          aria-pressed={currentLang === lang}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
            currentLang === lang
              ? "bg-background text-hb-navy-dark shadow-sm dark:text-white"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`button-lang-${lang}`}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
